import { type FormEvent, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { trackSignupStart } from '@lightning/analytics';
import { GoogleIcon, Input, LinkedInIcon } from '@lightning/ui';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';
import { beginGoogleSignIn, isBrandedGoogleSignInAvailable } from '../lib/googleSignIn';
import { beginLinkedInSignIn } from '../lib/linkedinSignIn';

export type AuthMode = 'sign-in' | 'sign-up';

interface LoginPageProps {
  mode?: AuthMode;
}

/** Where a returning user lands after signing in. */
export const POST_SIGN_IN_PATH = '/chat';
/** Where a brand-new account lands (email confirmation links return here too). */
export const POST_SIGN_UP_PATH = '/onboarding';

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep focus-visible:ring-offset-2';
const primaryButton = `flex h-12 w-full items-center justify-center gap-3 rounded-xl bg-how-space-deep px-4 text-sm text-how-paper-soft transition-colors hover:bg-how-space-mid disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`;
const secondaryButton = `flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-how-space-deep/15 px-4 text-sm text-how-space-deep transition-colors hover:border-how-space-deep/30 hover:bg-how-paper disabled:cursor-not-allowed disabled:opacity-50 ${focusRing}`;
const quietLink = `w-full pt-1 text-center text-xs text-how-text-secondary hover:text-how-space-deep ${focusRing}`;
const fieldClass =
  'h-12 rounded-xl border-how-space-deep/15 bg-transparent px-4 font-body text-base text-how-space-deep placeholder:text-how-text-secondary/70 focus-visible:ring-how-space-deep';

/**
 * One auth surface for both intents. `/login` and `/sign-in` render sign-in;
 * `/signup` and `/sign-up` render sign-up. Visuals are Ethos tokens only.
 */
export default function LoginPage({ mode = 'sign-in' }: LoginPageProps) {
  const isSignUp = mode === 'sign-up';
  const postAuthPath = isSignUp ? POST_SIGN_UP_PATH : POST_SIGN_IN_PATH;
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmationEmail, setConfirmationEmail] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user, loading: isAuthLoading, signIn } = useAuth();
  const posthog = usePostHog();
  const signupStartFiredRef = useRef(false);

  // A signed-in visitor never sees the form. This sits above the loading branch on purpose:
  // once auth has resolved with a user, the redirect wins even if a submit is still settling.
  if (user && !isAuthLoading && !isSubmitting) {
    return <Navigate to={postAuthPath} replace />;
  }

  // Signup-start is a funnel event; a plain sign-in must never inflate it. One ref covers the
  // email, Google and LinkedIn paths, so a retry on the same visit counts once.
  const fireSignupStart = () => {
    if (!isSignUp || signupStartFiredRef.current) return;
    signupStartFiredRef.current = true;
    void trackSignupStart('howie_auth_page', posthog);
  };

  const DUPLICATE_EMAIL = 'An account already exists for this email. Sign in instead, or reset your password.';

  // supabase-js exposes `code` on AuthError (v2.7x); the message substrings are the fallback for
  // older shapes and for errors raised elsewhere.
  const friendlyError = (caught: unknown) => {
    const code = (caught as { code?: string } | null)?.code;
    const message = caught instanceof Error ? caught.message : '';
    if (code === 'invalid_credentials' || message.includes('Invalid login credentials')) return 'Incorrect email or password.';
    if (code === 'email_not_confirmed' || message.includes('Email not confirmed')) return 'Please confirm your email before signing in.';
    if (code === 'user_already_exists' || message.includes('already registered')) return DUPLICATE_EMAIL;
    return message || `Unable to ${isSignUp ? 'create your account' : 'sign you in'}. Please try again.`;
  };

  const handleEmailSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    fireSignupStart();
    setIsSubmitting(true);
    setError(null);

    try {
      if (!isSignUp) {
        await signIn(email, password);
        navigate(postAuthPath, { replace: true });
        return;
      }

      const { data, error: signupError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName.trim(),
            app_name: 'howie',
          },
          emailRedirectTo: `${window.location.origin}${postAuthPath}`,
        },
      });

      if (signupError) throw signupError;
      // With email confirmation on, Supabase answers a duplicate email with an obfuscated user
      // that has no identities and no session, not with an error. Say so instead of promising mail.
      if (data.user && data.user.identities?.length === 0) {
        setError(DUPLICATE_EMAIL);
        return;
      }
      if (data.session) {
        navigate(postAuthPath, { replace: true });
      } else {
        setConfirmationEmail(email);
      }
    } catch (caughtError) {
      setError(friendlyError(caughtError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleAuth = async () => {
    fireSignupStart();
    setIsSubmitting(true);
    setError(null);

    try {
      // HOWIE's own consent screen (the HOWIE web OAuth client) when the build carries it;
      // otherwise Supabase's shared Google client, which shows Alexandria's name.
      if (isBrandedGoogleSignInAvailable()) {
        window.location.assign(await beginGoogleSignIn({ origin: window.location.origin, postAuthPath }));
        return;
      }
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}${postAuthPath}`,
        },
      });
      if (oauthError) throw oauthError;
    } catch (caughtError) {
      setError(friendlyError(caughtError));
      setIsSubmitting(false);
    }
  };

  const handleLinkedInAuth = async () => {
    fireSignupStart();
    setIsSubmitting(true);
    setError(null);
    try {
      // Supabase's LinkedIn (OIDC) provider; the browser leaves for LinkedIn and returns to /auth/linkedin/callback.
      await beginLinkedInSignIn({
        origin: window.location.origin,
        postAuthPath,
        signInWithOAuth: (credentials) => supabase.auth.signInWithOAuth(credentials),
      });
    } catch (caughtError) {
      setError(friendlyError(caughtError));
      setIsSubmitting(false);
    }
  };

  if (isAuthLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-how-paper-soft">
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }

  return (
    <main className="relative min-h-svh overflow-x-hidden bg-how-paper-soft px-5 py-8 text-how-space-deep sm:px-8">
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[46%] bg-gradient-to-b from-transparent to-how-rose opacity-70"
        aria-hidden="true"
      />

      <Link
        to="/"
        aria-label="Howie home"
        className="relative z-10 mx-auto flex w-fit rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep focus-visible:ring-offset-4"
      >
        <img src="/logo_green.svg" alt="Howie" className="h-7 w-auto" />
      </Link>

      <section className="relative z-10 mx-auto flex min-h-[calc(100svh-7rem)] w-full max-w-md flex-col items-center justify-center text-center">
        {confirmationEmail ? (
          <div className="animate-fade-in-up">
            <h1 className="font-voice text-3xl leading-tight sm:text-4xl">Check your inbox.</h1>
            <p className="mx-auto mt-5 max-w-sm text-base leading-7 text-how-text-secondary">
              We sent a confirmation link to {confirmationEmail}. Follow it to begin your Howie journey.
            </p>
            <Link
              to="/login"
              className="mt-8 inline-block text-sm text-how-space-deep underline decoration-how-accent-warm decoration-2 underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep focus-visible:ring-offset-4"
            >
              Return to sign in
            </Link>
          </div>
        ) : (
          <div className="w-full animate-fade-in-up">
            <p className="mb-5 text-xs uppercase tracking-[0.14em] text-how-text-secondary">
              {isSignUp ? 'Begin with Howie' : 'Welcome back'}
            </p>
            <h1 className="font-voice text-[2.5rem] leading-[1.06] tracking-[-0.02em] text-how-space-deep sm:text-5xl">
              <span className="block">Choose HOW</span>{' '}
              <span className="block">you show up.</span>
            </h1>

            <div className="mx-auto mt-10 w-full max-w-[340px] rounded-2xl border border-how-space-deep/10 bg-how-paper-soft/75 p-4 shadow-card backdrop-blur-sm sm:p-5">
              {error && (
                <div
                  role="alert"
                  className="mb-4 rounded-xl bg-how-rose px-4 py-3 text-left text-sm leading-5 text-how-space-deep"
                >
                  {error}
                </div>
              )}

              {showEmailForm ? (
                <form onSubmit={handleEmailSubmit} className="space-y-3 text-left">
                  {isSignUp && (
                    <Input
                      type="text"
                      value={fullName}
                      onChange={(event) => setFullName(event.target.value)}
                      required
                      autoComplete="name"
                      placeholder="Full name"
                      aria-label="Full name"
                      className={fieldClass}
                    />
                  )}
                  <Input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    autoComplete="email"
                    placeholder="Email"
                    aria-label="Email"
                    className={fieldClass}
                  />
                  <Input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    minLength={isSignUp ? 8 : undefined}
                    autoComplete={isSignUp ? 'new-password' : 'current-password'}
                    placeholder="Password"
                    aria-label="Password"
                    className={fieldClass}
                  />
                  <button type="submit" disabled={isSubmitting} className={primaryButton}>
                    {isSubmitting ? 'One moment…' : isSignUp ? 'Create account' : 'Sign in'}
                  </button>
                  {!isSignUp && (
                    <Link to="/forgot-password" className={`block ${quietLink}`}>
                      Forgot password?
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      setShowEmailForm(false);
                    }}
                    className={quietLink}
                  >
                    Back to options
                  </button>
                </form>
              ) : (
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => void handleGoogleAuth()}
                    disabled={isSubmitting}
                    className={primaryButton}
                  >
                    <GoogleIcon id={isSignUp ? 'howie_signup_google' : 'howie_login_google'} />
                    {isSignUp ? 'Continue with Google' : 'Sign in with Google'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleLinkedInAuth()}
                    disabled={isSubmitting}
                    className={secondaryButton}
                  >
                    <LinkedInIcon />
                    {isSignUp ? 'Continue with LinkedIn' : 'Sign in with LinkedIn'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      setShowEmailForm(true);
                    }}
                    disabled={isSubmitting}
                    className={secondaryButton}
                  >
                    <Mail size={18} aria-hidden="true" />
                    {isSignUp ? 'Continue with email' : 'Sign in with email'}
                  </button>
                </div>
              )}
            </div>

            <p className="mt-7 text-sm text-how-text-secondary">
              {isSignUp ? 'Already have an account?' : 'New to Howie?'}{' '}
              <Link
                to={isSignUp ? '/login' : '/signup'}
                className="text-how-space-deep underline decoration-how-accent-warm decoration-2 underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep focus-visible:ring-offset-4"
              >
                {isSignUp ? 'Sign in' : 'Sign up'}
              </Link>
            </p>
          </div>
        )}
      </section>
    </main>
  );
}
